import type { Id } from "@repo/convex/dataModel";
import { ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import { useCallback } from "react";
import { cn } from "@/lib/utils";
import { useProjectNavigate } from "../../routing/project-route-context";
import type {
  AssistantActivityTurnBlock,
  AssistantActivityTurnItem,
  AssistantActivityTurnTimelineItem,
} from "../../types";
import { AssistantActivityLabel } from "../assistant-activity-label";
import { AssistantAskUserAnswerDetails } from "../assistant-ask-user-summary";
import { AssistantContextCompactionDivider } from "../assistant-context-compaction-divider";
import { AssistantLinkedText } from "../assistant-linked-text";
import { AssistantMessageMarkdown } from "../assistant-message-markdown";
import { AssistantOperationApprovalCard } from "../assistant-operation-approval-card";
import { getActivityIcon, getAssistantActivityIconName } from "./activity-icons";
import {
  getSafeEntryLabels,
  isActiveWorkItem,
  type RenderableActivityBlock,
  toApprovalGate,
} from "./activity-model";
import { AnimatedCollapse } from "./animated-collapse";

function ActivityLine({ item, active }: { item: AssistantActivityTurnItem; active: boolean }) {
  const iconName = getAssistantActivityIconName(item);
  const Icon = getActivityIcon(iconName);
  const toneClass =
    item.kind === "error"
      ? "text-destructive"
      : item.kind === "warning"
        ? "text-amber-600 dark:text-amber-400"
        : "text-muted-foreground";

  return (
    <div className="flex items-start gap-2 text-xs leading-5 text-muted-foreground">
      <Icon
        className={cn("mt-0.5 size-3.5 shrink-0", toneClass)}
        data-activity-icon={iconName}
        aria-hidden="true"
      />
      <p className={cn("min-w-0 flex-1", item.kind === "error" ? "text-foreground" : toneClass)}>
        <AssistantActivityLabel active={active} parts={item.textParts}>
          {item.text}
        </AssistantActivityLabel>
      </p>
    </div>
  );
}

export function ActivityTurnItemRow({
  turn,
  item,
  projectId,
  compact,
}: {
  turn: AssistantActivityTurnTimelineItem;
  item: AssistantActivityTurnItem;
  projectId: Id<"projects">;
  compact: boolean;
}) {
  const navigate = useProjectNavigate();

  if (item.kind === "context_compaction" && item.contextCompaction) {
    return (
      <AssistantContextCompactionDivider
        item={{ text: item.text, status: item.contextCompaction.status }}
        compact={compact}
        embedded
      />
    );
  }

  if (item.kind === "preamble" || item.kind === "assistant_progress") {
    // The note is the model's own sentence, the same text the live note showed while it
    // streamed, so it renders through the assistant-body renderer and reads identically settled
    // and live: markdown, mention chips and evidence chips alike.
    return (
      <AssistantMessageMarkdown
        content={item.text}
        density={compact ? "compact" : "default"}
        onNavigate={navigate}
      />
    );
  }

  const approvalGate = toApprovalGate(turn, item);
  if (approvalGate) {
    return (
      <AssistantOperationApprovalCard projectId={projectId} item={approvalGate} compact={compact} />
    );
  }

  if (item.kind === "user_input" && item.userInput && item.userInput.status !== "pending") {
    return (
      <div className="space-y-2" data-testid="assistant-ask-user-summary">
        <ActivityLine item={item} active={false} />
        {item.userInput.questions.length > 0 ? (
          <div className="ml-5 min-w-0 max-w-full overflow-hidden">
            <AssistantAskUserAnswerDetails
              status={item.userInput.status}
              questions={item.userInput.questions}
              answers={item.userInput.answers}
            />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <ActivityLine item={item} active={turn.status === "in_progress" && isActiveWorkItem(item)} />
  );
}

function ActivityItemDetails({
  turn,
  item,
  projectId,
  compact,
}: {
  turn: AssistantActivityTurnTimelineItem;
  item: AssistantActivityTurnItem;
  projectId: Id<"projects">;
  compact: boolean;
}) {
  const entryLabels = getSafeEntryLabels(item);
  return (
    <div className="space-y-2">
      <ActivityTurnItemRow turn={turn} item={item} projectId={projectId} compact={compact} />
      {entryLabels.length > 1 ? (
        <div className="ml-5 space-y-1 text-xs leading-5 text-muted-foreground">
          {entryLabels.map((label, index) => (
            <p key={`${item.id}:entry:${index}`}>{label}</p>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ActivityBlockSummaryRow({
  block,
  iconSourceItem,
  expanded,
  onToggle,
}: {
  block: AssistantActivityTurnBlock;
  iconSourceItem: AssistantActivityTurnItem | null;
  expanded: boolean;
  onToggle: () => void;
}) {
  const iconName = iconSourceItem ? getAssistantActivityIconName(iconSourceItem) : "square-pen";
  const Icon = getActivityIcon(iconName);
  const summaryLabel = (
    <span className="min-w-0 flex-1">
      <AssistantLinkedText parts={block.summaryTextParts} fallback={block.summaryText ?? ""} />
    </span>
  );

  if (block.collapsible && block.summaryTextParts?.length) {
    return (
      <div className="group flex w-full items-start gap-2 rounded-sm text-left text-xs leading-5 text-muted-foreground transition-colors hover:text-foreground">
        <button
          type="button"
          className="rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse activity details" : "Expand activity details"}
        >
          {expanded ? (
            <ChevronDown aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          ) : (
            <ChevronRight aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          )}
        </button>
        <Icon
          className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
          data-activity-icon={iconName}
          aria-hidden="true"
        />
        {summaryLabel}
      </div>
    );
  }

  const content = (
    <>
      {block.collapsible ? (
        expanded ? (
          <ChevronDown aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
        ) : (
          <ChevronRight aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
        )
      ) : (
        <span className="size-3.5 shrink-0" />
      )}
      <Icon
        className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
        data-activity-icon={iconName}
        aria-hidden="true"
      />
      {summaryLabel}
    </>
  );

  if (!block.collapsible) {
    return (
      <div className="flex items-start gap-2 text-xs leading-5 text-muted-foreground">
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      className="group flex w-full items-start gap-2 rounded-sm text-left text-xs leading-5 text-muted-foreground transition-colors hover:text-foreground"
      onClick={onToggle}
      aria-expanded={expanded}
    >
      {content}
    </button>
  );
}

export function ActivityBlock({
  turn,
  renderableBlock,
  projectId,
  compact,
  expanded,
  onToggleBlock,
}: {
  turn: AssistantActivityTurnTimelineItem;
  renderableBlock: RenderableActivityBlock;
  projectId: Id<"projects">;
  compact: boolean;
  expanded: boolean;
  onToggleBlock: (blockId: string) => void;
}) {
  const { block, headerItem, workItems } = renderableBlock;
  const iconSourceItem = workItems[0] ?? headerItem;
  const hasSummary = Boolean(block.summaryText);
  const showSummary = hasSummary && (block.collapsible || workItems.length === 0);
  const showDetails = !block.collapsible || !hasSummary;
  const handleToggle = useCallback(() => {
    onToggleBlock(block.id);
  }, [block.id, onToggleBlock]);

  return (
    <div className="space-y-3" data-testid="assistant-activity-block" data-block-id={block.id}>
      {headerItem ? (
        <ActivityTurnItemRow
          turn={turn}
          item={headerItem}
          projectId={projectId}
          compact={compact}
        />
      ) : null}

      {showSummary ? (
        <ActivityBlockSummaryRow
          block={block}
          iconSourceItem={iconSourceItem}
          expanded={expanded}
          onToggle={handleToggle}
        />
      ) : null}

      {block.collapsible ? (
        <AnimatedCollapse open={expanded} enterDurationMs={170} exitDurationMs={140}>
          <div className="ml-8 space-y-3 pt-2" data-testid="assistant-activity-block-details">
            {workItems.map((workItem) => (
              <ActivityItemDetails
                key={workItem.id}
                turn={turn}
                item={workItem}
                projectId={projectId}
                compact={compact}
              />
            ))}
          </div>
        </AnimatedCollapse>
      ) : showDetails ? (
        <div className={cn("space-y-3", showSummary && "pt-1")}>
          {workItems.map((workItem) => (
            <ActivityItemDetails
              key={workItem.id}
              turn={turn}
              item={workItem}
              projectId={projectId}
              compact={compact}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ThinkingFooter() {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Sparkles className="size-3.5 shrink-0" aria-hidden="true" />
      <AssistantActivityLabel active>Thinking</AssistantActivityLabel>
    </div>
  );
}

export function NarrationLine({ text, compact }: { text: string; compact: boolean }) {
  const navigate = useProjectNavigate();
  return (
    <div data-testid="assistant-activity-narration">
      <AssistantMessageMarkdown
        content={text}
        density={compact ? "compact" : "default"}
        onNavigate={navigate}
      />
    </div>
  );
}
